'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import type { Module } from '@/types/auth';
import ThreeDotLoader from '@/components/common/ThreeDotLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredModule?: Module;
}

export default function ProtectedRoute({ children, requiredModule }: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, hasPermission, permissionsReady } = useAuth();
  const router = useRouter();
  const accessDenied = !isLoading && isAuthenticated && permissionsReady
    && !!requiredModule && !hasPermission(requiredModule);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      router.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (accessDenied) {
      router.replace('/unauthorized');
    }
  }, [isLoading, isAuthenticated, accessDenied, router]);

  // RBAC menuKeys 尚未返回时不做权限判断:空 user.modules 回退会误判无权限,
  // 造成"闪一下未登录/无权限再被弹回"的体验
  if (isLoading || !permissionsReady) {
    return (
      <div className="flex h-[calc(100vh-70px)] items-center justify-center">
        <ThreeDotLoader />
      </div>
    );
  }

  if (!isAuthenticated || accessDenied) return null;

  return <>{children}</>;
}
